import { apiClient } from '@/lib/apiClient';

export interface TopProspect {
  id: number;
  name: string;
  current_stage: string;
  assigned_to: number;
  country: string;
  last_activity: string;
  last_activity_date?: string;
  last_activity_updated_at: string;
  created_by: number;
  created_by_name?: string;
  assigned_to_name?: string;
  created_at: string;
}

export interface TopProspectsResponse {
  prospects: TopProspect[];
}

class TopProspectsService {
  async getTopProspects(userId?: string): Promise<TopProspect[]> {
    try {
      const endpoint = userId && userId !== 'all' 
        ? `/leadsys/prospact?user_id=${userId}`
        : `/leadsys/prospact`;
        
      const data = await apiClient.get<TopProspect[]>(endpoint);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching top prospects:', error);
      throw error;
    }
  }

  // Helper method to get stage color
  getStageColor(stage: string): string {
    const stageColors: Record<string, string> = {
      'New': 'bg-blue-100 text-blue-800 border-blue-200',
      'Contacted': 'bg-purple-100 text-purple-800 border-purple-200', 
      'Connected': 'bg-lime-100 text-lime-800 border-lime-200',
      'Qualified': 'bg-green-100 text-green-800 border-green-200',
      'Negotiation': 'bg-orange-100 text-orange-800 border-orange-200',
      'Proposal': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Trial Received': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Closed Won': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Closed Lost': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return stageColors[stage] || 'bg-gray-100 text-gray-800 border-gray-200';
  }

  // Helper method to get urgency level based on last activity
  getUrgencyLevel(lastActivity: string): 'high' | 'medium' | 'low' {
    const daysSinceActivity = Math.floor(
      (Date.now() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24)
    );
    
    if (daysSinceActivity > 7) return 'high';
    if (daysSinceActivity > 3) return 'medium';
    return 'low';
  }

  // Helper method to parse activity type and status
  parseActivity(lastActivity: string): { type: string; status: string } {
    if (!lastActivity || !lastActivity.includes(':')) {
      return { type: 'Unknown', status: 'Unknown' };
    }
    
    const [type, status] = lastActivity.split(':');
    
    const typeMap: Record<string, string> = {
      'call': 'Call',
      'email': 'Email', 
      'text': 'Text',
      'meeting': 'Meeting',
      'note': 'Note'
    };
    
    const statusMap: Record<string, string> = {
      'completed': 'Completed',
      'no_answer': 'No Answer',
      'voice_mail': 'Voice Mail',
      'sent': 'Sent',
      'opened': 'Opened',
      'replied': 'Replied',
      'gk': 'GK'
    };
    
    return {
      type: typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1),
      status: statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1)
    };
  }

  // Helper method to get activity status color
  getActivityStatusColor(status: string): string {
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'completed':
      case 'replied':
        return 'text-green-600';
      case 'gk':
      case 'no answer':
        return 'text-yellow-600';
      case 'voice mail':
        return 'text-purple-600';
      case 'sent':
      case 'opened':
        return 'text-blue-600';
      default:
        return 'text-muted-foreground';
    }
  }
}

export const topProspectsService = new TopProspectsService();